<template>
  <div ref="sliderContainer" class="custom-range-slider">
    <input
      v-model="valueModel"
      :aria-label="label"
      :aria-labelledby="ariaLabelledby && !label ? ariaLabelledby : undefined"
      type="range"
      :min="min"
      :max="max"
      :step="step"
      :style="{ '--progress-percent': `${progressPercent}%` }"
    />
    <div
      class="custom-range-slider-thumb"
      :style="{ left: `${thumbPosition}%` }"
    >
      <span
        v-if="showValue"
        class="custom-range-slider-thumb-value"
        v-text="valueModel"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'RangeSlider',

  props: {
    ariaLabelledby: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    step: {
      type: Number,
      default: 1,
    },
    modelValue: {
      type: Number,
      default: 0,
    },
    showValue: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:modelValue'],

  computed: {
    valueModel: {
      get() {
        return this.modelValue;
      },
      set(value: number) {
        const newValue = Math.max(this.min, value);
        this.$emit('update:modelValue', newValue);
      },
    },

    roundedValue(): number {
      return Math.round(this.valueModel / this.step) * this.step;
    },

    progressPercent(): number {
      const range = this.max - this.min;

      if (range <= 0) {
        return 0;
      }

      return ((this.roundedValue - this.min) / range) * 100;
    },

    thumbPosition(): number {
      const range = this.max - this.min;
      const adjustedValue = this.roundedValue - this.min;
      const thumbRadius = 24; // 16 * 1.5 assuming 16px is the root font size;
      const container = this.$refs.sliderContainer as HTMLElement;
      const containerWidth = container?.offsetWidth ?? 0;

      if (range <= 0) {
        return 0;
      }

      // Calculate the available space for the thumb to move (container width minus thumb width)
      // This is the space that the thumb can move within the container
      const availableSpaceDifference =
        containerWidth > thumbRadius
          ? (containerWidth - thumbRadius) / containerWidth
          : 1;

      return (adjustedValue / range) * availableSpaceDifference * 100;
    },
  },

  created() {
    // Validate props
    if (this.min >= this.max) {
      console.warn('RangeSlider: max value must be greater than min value');
      return;
    }

    // Initialize value to min value if not already set
    if (
      !this.modelValue ||
      this.modelValue < this.min ||
      this.modelValue > this.max
    ) {
      this.$emit('update:modelValue', this.min);
    }
  },
});
</script>
